//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import {
  BodySecondaryText,
  LinkPrimaryText,
} from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { ItemStats } from '@uia/details/ItemStatsUtils';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

import { InstagramWebLinks } from 'app/constants/social-links';

export interface StatsAndLocationProps {
  itemStats: ItemStats[];
  instagramLocationName?: string;
  instagramLocationId?: string;
}

export const StatsAndLocation = ({
  instagramLocationId,
  instagramLocationName,
  itemStats,
}: StatsAndLocationProps) => (
  <Wrapper>
    <Stats>
      {itemStats.map((stats) => (
        <li key={stats.name}>
          <SmallTooltip
            content={stats.tooltip}
            target={
              <StatsContent>
                <Icon name={stats.icon} />
                {stats.value}
              </StatsContent>
            }
          />
        </li>
      ))}
    </Stats>
    {isDefined(instagramLocationId) && (
      <Location title={instagramLocationName}>
        <Icon name='location' />
        <Link
          href={InstagramWebLinks.getLocationLink(instagramLocationId)}
          rel='noreferrer'
          target='_blank'
          trackingId={UIATrackingIds.DETAILS_OPEN_LOCATION}
        >
          {instagramLocationName}
        </Link>
      </Location>
    )}
  </Wrapper>
);

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
    gap: ${theme.spacings[2]};
  `,
);

const Stats = styled.ul(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[2]};
    margin: ${theme.spacings[0]};
    padding: ${theme.spacings[0]};
    list-style: none;
  `,
);

const StatsContent = styled(BodySecondaryText)(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);

const Location = styled(LinkPrimaryText)(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);

const Link = withTracking(
  styled.a(
    () => `
      && {
        cursor: pointer;
  
        :hover {
          text-decoration: none;
        }
      }
    `,
  ),
);
